import axios from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { getBrand } from 'pared/utils/brand'
import { getUserPersistedJwtToken } from 'pared/utils/user'

import { getUser } from '../../../utils/user'
import { IPropsType } from './types'
import { getBrandCodeForDb } from './utils'

export const useChatBox = ({
  agentCode,
  sendStoreId,
}: Pick<IPropsType, 'agentCode' | 'sendStoreId'>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const expoSousChefUrl = process.env.REACT_APP_EXPO_SOUS_CHEF_URL
  const backendBaseUrl = process.env.REACT_APP_BE_BASE_URL || ''
  const brandCode = backendBaseUrl.includes('expo-demo')
    ? 'expo_demo'
    : getBrand()
  const brandCodeDb = getBrandCodeForDb(brandCode)
  const iframeOrigin = `${expoSousChefUrl}/${brandCodeDb}`

  useEffect(() => {
    const fetchShortToken = async () => {
      const jwt = getUserPersistedJwtToken()
      if (!jwt) return

      const { email } = getUser()
      if (!email) return

      try {
        const res = await axios.post(
          `${backendBaseUrl}/sous-chef/generate-short-token`,
          { email },
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          },
        )

        const shortToken = res.data.token
        // browsers never send the fragment to the server
        const fullUrl = `${iframeOrigin}?agentCode=${agentCode}#token=${shortToken}`
        setIframeUrl(fullUrl)
      } catch (err) {
        console.error('Failed to fetch short token:', err)
      }
    }

    fetchShortToken()
  }, [agentCode, backendBaseUrl, iframeOrigin])

  const userInfo = getUser()
  const location = useLocation()
  const store = useMemo(() => {
    const queryObject = new URLSearchParams(location.search)
    return queryObject.get('store') || userInfo.defaultLocationId?.toString()
  }, [location.search, userInfo])

  const sendMessageToIframe = useCallback(
    (storeId: string) => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ storeId }, iframeOrigin)
      }
    },
    [iframeOrigin],
  )

  const [iframeReady, setIframeReady] = useState(false)

  useEffect(() => {
    const handleIframeLoad = () => {
      setTimeout(() => {
        setIframeReady(true)
      }, 500)
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin === iframeOrigin &&
        event.data?.type === 'IFRAME_READY'
      ) {
        setIframeReady(true)
      }
    }

    window.addEventListener('message', handleMessage)
    const currentIframeRef = iframeRef.current
    if (currentIframeRef) {
      currentIframeRef.addEventListener('load', handleIframeLoad)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
      if (currentIframeRef) {
        currentIframeRef.removeEventListener('load', handleIframeLoad)
      }
    }
  }, [iframeOrigin])

  useEffect(() => {
    if (sendStoreId && store && iframeReady) {
      sendMessageToIframe(store)
    }
  }, [sendStoreId, store, iframeReady, sendMessageToIframe])

  return {
    isOpen,
    setIsOpen,
    isExpanded,
    setIsExpanded,
    iframeUrl,
    iframeRef,
    userInfo,
  }
}
